import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MicNoneOutlinedIcon from '@mui/icons-material/MicNoneOutlined';
import PauseOutlinedIcon from '@mui/icons-material/PauseOutlined';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import CallEndIcon from '@mui/icons-material/CallEnd';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import { styled } from '@mui/material/styles';
import logo from "../../assets/dafabet.png";
import logoBig from "../../assets/dafabetBig.png";
import { selectConfig } from '../../redux/slices/configSlice';
import { selectPlayers} from '../../redux/slices/playersSlice';

import { useSelector } from 'react-redux';
import { endVoiceBBCard } from '../../services';

declare global {
    interface Window {
        livechat: any;
        LiveChatAvCallTypes?: any;
        LiveChatAvErrorCodes?: any;
    }
}

const CustomIconButton = styled(IconButton)(({ theme }) => ({
    backgroundColor: '#FFE000',
    color: '#000',
    borderRadius: "50%",
    width: "80px",
    height: "80px",
    marginTop: "60px"
}));




export default function Call() {
    const [call, setCall] = useState<any>(null);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [isCallEnded, setIsCallEnded] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [elapsedTime, setElapsedTime] = useState(0); // Time in seconds
    const [username, setUsername] = useState<string>('');
    const [email, setEmail] = useState<string>('');

    const config = useSelector(selectConfig);
    const formData = config?.data ? config.data[0].attributes : null;

    const players = useSelector(selectPlayers);
    const playerData = players?.data ? players?.data.attributes : null;

    let bbc_id: string | null = null;

    const initiateCallEndBBCard = async () => {
        await endVoiceBBCard({ bbc_id });
    };


    useEffect(() => {
        const sessionCode = new URLSearchParams(window.location.search).get("s");
    
        if (sessionCode) {
            setIsAuthorized(true);
    
            // Since scripts are already loaded statically,
            // we can directly call initialization
            if (window.livechat) {
                initializeAVSettings();
                window.livechat.Open();
                setupLivechatEvents();
            } else {
                console.error("window.livechat is not available.");
            }
        }
    }, []);

    useEffect(() => {
        let timer: NodeJS.Timeout | undefined;
        if (call) {
            timer = setInterval(() => {
                setElapsedTime((prev) => prev + 1);
            }, 1000);
        }
        return () => {
            if (timer) clearInterval(timer);
        };
    }, [call]);

    useEffect(() => {
            setUsername(playerData?.username || '');
            setEmail(playerData?.email || '');
        }, [playerData]);

    const initializeAVSettings = () => {
        const config = {
            MediaTransport: {
                iceServers: [
                    { urls: 'turn:session.msw.ph:3579', credential: 'avaya@123', username: 'avaya' }
                ],
                iceTransportPolicy: "relay"
            },
            MediaCapture: {
                audio: { enabled: true },
                video: { enabled: false, width: 640, height: 480 }
            },
            Display: {
                OnConnected: (rs: MediaStream[], ls: MediaStream[][]) => {
                    const remoteVid = document.getElementById("remote-vid") as HTMLVideoElement | null;
                    const sourceVid = document.getElementById("source-vid") as HTMLVideoElement | null;
                
                    if (remoteVid && sourceVid) {
                        remoteVid.srcObject = rs[0];
                        sourceVid.style.display = "";
                        sourceVid.srcObject = ls[0][0];
                    }
                },
                OnDisconnected: () => {
                    window.livechat.EndAvCall();
                
                    const remoteVid = document.getElementById("remote-vid") as HTMLVideoElement | null;
                    const sourceVid = document.getElementById("source-vid") as HTMLVideoElement | null;
                    const videoDisplay = document.getElementById("videoDisplay") as HTMLElement | null;
                
                    if (remoteVid) remoteVid.srcObject = null;
                    if (sourceVid) sourceVid.srcObject = null;
                    if (videoDisplay) videoDisplay.style.display = "none";
                
                    setIsCallEnded(true);
                },
                
                OnFail: (errorCode: number) => {
                    let msg = "Audio Error";
                    const errorCodes = window.LiveChatAvErrorCodes;
                
                    switch (errorCode) {
                        case errorCodes?.AUDIO_VIDEO_CAPTURE_ERROR:
                            msg = "Audio Blocked Permission";
                            break;
                        case errorCodes?.SYSTEM_ERROR:
                            msg = "Audio System Error";
                            break;
                    }
                
                    console.error(msg);
                    window.livechat.End(`Call ended due to ${msg}`);
                    setIsCallEnded(true);
                }
            }
        };

        window.livechat.SetAvConfigs(config);
        console.log("AV settings initialized");
    };

    const setupLivechatEvents = () => {
        const lc = window.livechat;

        lc.OnRemoteUserConnected = () => {
            console.log("[LiveChat] OnRemoteUserConnected");
            const av_config = {
                call: window.LiveChatAvCallTypes?.AUDIO,
                startAudioMuted: false,
                startVideoMuted: false
            };
            lc.StartAvCall('', '', av_config);
            setTimeout(() => {
                setCall(true);
                document.getElementById("btn-mute-call")?.removeAttribute("disabled");
                document.getElementById("btn-pause-call")?.removeAttribute("disabled");
                document.getElementById("btn-end-call")?.removeAttribute("disabled");
            }, 2000);
        };

        lc.OnServerStateChanged = (state: string) => {
            console.log("[LiveChat] OnServerStateChanged:", state);
            if (state !== "online") {
                lc.EndAvCall();
                lc.End("User disconnected due to offline API");
                setIsCallEnded(true);
            }
        };

        lc.OnOpenFailed = (retCode: string) => {
            console.log("[LiveChat] OnOpenFailed:", retCode);
            lc.slog?.('info', "OnOpenFailed", "Return code: " + retCode);
            if (retCode === "NoSessionFound") {
                lc.GetAccessToken?.(
                    (token: string) => {
                        OnStart(token);
                    },
                    (msg: any) => {
                        console.error("GetAccessToken Error:", msg);
                    }
                );
            } else if (retCode === "CookiesDisabled") {
                alert("Please enable browser Cookies");
            } else if (retCode === "AlreadyEndedSession") {
                alert("Already Ended Session");
            }
        };
        
        lc.OnStartSuccess = () => {
            console.log("[LiveChat] OnStartSuccess");
            lc.slog?.("info", "OnStartSuccess", "Livechat: StartSuccess got initiated");
        };
        lc.OnCallQueued = () => {
            console.log("[LiveChat] OnCallQueued");
            setTimeout(() => {
                document.getElementById("btn-end-call")?.removeAttribute("disabled");
            }, 2000);
        };

        lc.OnStartFailed = (errorCode: string) => {
            console.log("[LiveChat] OnStartFailed:", errorCode);
            lc.slog?.("error", "livechat.OnStartFailed", errorCode);
            if (errorCode === "MaxConcurrentSessionCountReached") {
                // Optional: Show a waiting page
                console.log("Maximum concurrent sessions reached");
                // loadPage('waiting-agent'); // If you replicate loadPage
            } else {
                console.error(`${errorCode} failed error`);
                // Replace this with your error handling logic or state
                alert("Something went wrong starting the call. Please try again.");
            }
        };
        
        lc.OnOpenSuccess = (session: any) => {
            console.log("[LiveChat] OnOpenSuccess", session);
            lc.slog?.("info", "livechat.OnOpenSuccess", "Resumed Session");
            lc.End("Closed Unended Session");
            alert("Session not properly closed. Page will be reloaded.");
            window.location.reload();
        };
        
        lc.OnEnd = (initiator: string, reason: string) => {
            console.log(`[LiveChat] OnEnd | By: ${initiator} | Reason: ${reason}`);
            // Replace with your async cleanup (e.g. Axios or fetch)
            initiateCallEndBBCard();
        
            lc.slog?.("info", "livechat.OnEnd", `Call Ended by ${initiator} | Reason: ${reason}`);
            lc.EndAvCall();
            setCall(false);
            setIsCallEnded(true);
        
            // Handling UI (you can replace with setState and conditional JSX)
            if ((initiator === "server" || initiator === "lib") && (reason === "closed" || reason === "All agents disconnected")) {
                // If you use a `showSurvey` state, you can show survey component here
                const surveyAvailable = false; // Replace with real survey flag
                if (surveyAvailable) {
                    // Example: setPage('survey');
                    alert("Survey would be shown now.");
                } else {
                    // Example: setPage('call-ended');
                    console.log("Call ended, show thank you.");
                }
            } else {
                // Example: setPage('error');
                console.error("Unknown disconnect reason.");
            }
        
            // Session count control
            const reloadLbl = document.getElementById('lbl-call-again');
            const reloadBtn = document.getElementById('btn-call-again');
            let mySession = Number(localStorage.getItem('mySession') || 0);
        
            if (mySession <= 2) {
                mySession += 1;
                // Optionally show retry button
                // if (reloadBtn) reloadBtn.style.display = "inline-block";
            } else {
                // After 3 redials, hide buttons
                if (reloadLbl) reloadLbl.style.display = "none";
                if (reloadBtn) reloadBtn.style.display = "none";
                mySession = 0;
            }
        
            localStorage.setItem('mySession', String(mySession));
        };
    };

    const OnStart = (token: string) => {
        const params = new URLSearchParams(window.location.search);
        let lang = params.get("lang") || "EN";
        let pathname = window.location.pathname;
        let url = pathname.split("/");
        let brandName;
        let vip_level = params.get('level');

        if(url[4]) {
            brandName = url[4].charAt(0).toUpperCase() + url[4].slice(1);
            brandName = (brandName == "Mc-desktop") ? "Dafa" : brandName;
        } else {
            brandName = "Dafa";
        }

        if (lang === 'EN') lang = 'IN';
        if (brandName === 'Zedbet') lang = 'TH';

        let backPart;
        if (username !== '') {
            backPart = 'CS_Arion_REG';
            
            if (brandName === 'Zedbet') {
                backPart = 'CS_WEB_REG';
            }
            if (lang === 'SC' && (vip_level === '13' || vip_level === '18')) {
                backPart = 'CS_Arion_Elite';
            }
        } else {
            backPart = 'CS_WEB_REG';
        }
        if (window.location.hostname === 'm.istg-chat.elysium-dfbt.com' && lang !== 'SC') {
            backPart = 'CS_Pre_Prod_Arion_REG';
        }
        const pLangVal = [lang, brandName, backPart].join('_');

        const info = {
            eCustDetails: {
                eCustName: "priyantha",
                eEmail: "tpwsinghe@gmail.com",
                eMobileNo: "",
                pLang: "hi",
                eAccessToken: token,
                pGroup: "HI_Dafa_CS_Arion_REG",
                uuid: "priyantha",
                eAuthLevel: "",
                eAuthStatus: "",
                eCin: "",
                eCustSeg: "",
                eKCIN: "",
                eRegStatus: "",
                pBranding: "",
                pChannel: "",
                pCountry: "",
                pKaiSid: "",
                pPageTag: "",
                pTopic: ""

                //eCustName: username,
                //eEmail: email,
                //eMobileNo: "",
                //pLang: lang,
                //eAccessToken: token,
                //pGroup: "IN_Dafa_CS_WEB_REG", // pLangVal You can customize this with dynamic brandLangGroup like `pLangVal`
                //uuid: username,
            }
        };
        
        console.log(info);
        const lc = window.livechat;
    
        lc.Start(info, '');
    
        lc.OnRemoteUserConnected = (name: string, agentId: string, sessionId: string) => {
            setTimeout(() => {
                try {
                    const av_config = {
                        call: window.LiveChatAvCallTypes?.AUDIO,
                        startAudioMuted: false,
                        startVideoMuted: false,
                    };
    
                    lc.slog?.("info", "livechat.OnRemoteUserConnected", `Agent: ${agentId} | Session: ${sessionId}`);
                    lc.StartAvCall('', '', av_config);
    
                    setTimeout(() => {
                        const connectingLabel = document.getElementById('connecting-label');
                        const msgLabel = document.getElementById('msg-label');
                        const callStatus = document.getElementById('call-status');
                        const muteBtn = document.getElementById('btn-mute-call');
                        const pauseBtn = document.getElementById('btn-pause-call');
                        const endBtn = document.getElementById('btn-end-call');
                        const callText = document.getElementById('call-btn-text');
    
                        if (connectingLabel) connectingLabel.style.display = "none";
                        if (msgLabel) msgLabel.style.display = "none";
                        if (callStatus) callStatus.style.display = "block";
                        if (muteBtn) muteBtn.removeAttribute("disabled");
                        if (pauseBtn) pauseBtn.removeAttribute("disabled");
                        if (endBtn) endBtn.removeAttribute("disabled");
                        if (callText) callText.style.display = "block";
    
                        // If you have a timer function to reflect real-time
                        // timer(); // optionally implement in React useEffect
                    }, 10000);
                } catch (e) {
                    lc.slog?.("error", "OnRemoteUserConnected", e);
                    console.error(e);
                }
            }, 100);
        };
    };

    const formatTime = (seconds: number): string => {
        const min = String(Math.floor(seconds / 60)).padStart(2, '0');
        const sec = String(seconds % 60).padStart(2, '0');
        return `${min}:${sec}`;
    };

    const toggleMute = () => {
        setIsMuted(!isMuted);
        window.livechat?.Pause(isMuted, isMuted);
    };

    const endCall = () => {
        window.livechat?.End("User ended call");
        setCall(false);
        setIsCallEnded(true);
    };


    return (
        <Box
            sx={{
                backgroundColor: "primary.main",
                width: "100%",
                height: "100%",
                position: "absolute",
                display: "flex",
                justifyContent: "space-between",
                top: "0",
                padding: "16px",
            }}
        >
            {!isAuthorized ? (
                <Box sx={{ textAlign: "center", width: "100%", display: "flex", flexDirection: "column", justifyContent: "space-around" }}>


                    <Button variant="outlined" sx={{
                        color: "secondary.light",
                        border: "3px solid",
                        borderRadius: "50%",
                        minWidth: "auto",
                        width: "35px",
                        height: "35px",
                        position: "absolute",
                        right: "16px",
                        top: "16px"

                    }}>
                        <CloseIcon />
                    </Button>

                    <Box>
                        <Box
                            component="img"
                            sx={{
                                width: "80%",
                                maxWidth: "380px",
                                height: "fit-content"
                            }}
                            alt="Sample image"
                            src={logo}

                        />

                        <Typography sx={{ color: "#fff", fontWeight: "bold" }} mt={4} mb={2} fontSize={24}>{ formData?.WebRTC?.UnAuthWndowText }</Typography>

                        <Button variant='contained' 
                            onClick={() => {
                                window.location.href = "https://www.dafabet.com/en/"; // Redirect to the desired URL
                            }}
                            sx={{ background: "#fff", borderRadius: "24px", color: "#000", fontWeight: "bold", textTransform: "capitalize", fontSize: "18px" }} >{ formData?.WebRTC?.LoginButtonText } </Button>

                    </Box>

                    <Box>
                        <Typography sx={{ color: "#fff", fontWeight: "bold" }} mt={4} mb={2} fontSize={24}>{ formData?.WebRTC?.UnAuthWndowText2 }</Typography>
                        <Button 
                        onClick={() => {
                            window.location.href = formData?.WebRTC?.UnAuthChatUrl || '/';
                        }}
                        variant='contained' sx={{ background: "#FFE000", color: "#000", fontWeight: "bold", textTransform: "capitalize", fontSize: "18px", width: "300px" }}>{ formData?.WebRTC?.UnAuthChatButtonText }</Button>
                    </Box>

                </Box>

            ) : null}


            {isAuthorized &&
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
                    <Box sx={{ textAlign: "center" }}>
                        <Box
                            component="img"
                            sx={{
                                width: "60%",
                                maxWidth: "380px",
                                height: "fit-content"
                            }}
                            alt="Sample image"
                            src={logoBig}

                        />

                        {!isCallEnded &&

                            <Box>

                                {!call &&

                                    <Typography sx={{ color: "#FFE000" }} mt={4} mb={2} fontSize={24}>{ formData?.WebRTC?.ConnectingText }</Typography>
                                }

                                {call &&
                                    <Box>
                                        <Typography sx={{ color: "#FFE000", textTransform: "uppercase", fontWeight: "bold" }} mt={2} mb={1} fontSize={36}>PRINCY</Typography>
                                        <Typography sx={{ color: "#FFE000" }} fontSize={24}>calling via Support Line</Typography>

                                        <Typography sx={{ color: "#FFF", textTransform: "uppercase", fontWeight: "bold" }} fontSize={40} mt={4}>
                                            {formatTime(elapsedTime)}
                                        </Typography>
                                    </Box>
                                }


                                <Box sx={{ display: "flex", justifyContent: "space-around" }} mt={4}>

                                    <Button variant="outlined" onClick={toggleMute} sx={{
                                        color: "secondary.light",
                                        border: "3px solid",
                                        borderRadius: "50%",
                                        minWidth: "auto",
                                        width: "80px",
                                        height: "80px"
                                    }}>
                                        <MicNoneOutlinedIcon sx={{ fontSize: "20px", width: "auto", height: "auto" }} />
                                    </Button>

                                    <Button variant="outlined" sx={{
                                        color: "secondary.light",
                                        border: "3px solid",
                                        borderRadius: "50%",
                                        minWidth: "auto",
                                        width: "80px",
                                        height: "80px"
                                    }}>
                                        <PauseOutlinedIcon sx={{ fontSize: "20px", width: "auto", height: "auto" }} />
                                    </Button>
                                    <Button variant="outlined" sx={{
                                        color: "secondary.light",
                                        border: "3px solid",
                                        borderRadius: "50%",
                                        minWidth: "auto",
                                        width: "80px",
                                        height: "80px"
                                    }}>
                                        <VolumeUpOutlinedIcon sx={{ fontSize: "20px", width: "auto", height: "auto" }} />
                                    </Button>

                                </Box>

                                <CustomIconButton onClick={endCall}>
                                    <CallEndIcon sx={{ fontSize: "20px", width: "auto", height: "auto" }} />
                                </CustomIconButton>


                            </Box>



                        }

                        {isCallEnded &&

                            <Box>
                                <Typography sx={{ color: "#FFF", textTransform: "capitalize", fontWeight: "bold", padding: "0 4px" }} fontSize={36} mt={3}>{ formData?.WebRTC?.ThankYouMessage }</Typography>

                                <Typography sx={{ color: "#FFE000" }} mt={6} fontSize={24}>{ formData?.WebRTC?.PlaceAnotherCallText }</Typography>

                                <CustomIconButton onClick={() => window.location.reload()}>
                                    <LocalPhoneIcon sx={{ fontSize: "20px", width: "auto", height: "auto" }} />
                                </CustomIconButton>

                            </Box>

                        }

                    </Box>
                </Box>}




        </Box>
    );
}
